import React, { useEffect, useState } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  ArchiveBoxIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { AuditCsvExportButton } from './AuditCsvExportButton';
import { documentWorkflowService, type DocumentWorkflowAuditDto } from '../services/documentWorkflowService';
import { WorkflowStatusBadge } from '../utils/workflowStatusBadge';
import {
  parseWorkflowAuditDetails,
  workflowActionLabel,
  workflowActionTone,
} from '../utils/workflowAuditDisplay';
import { useToast } from './UI/ToastProvider';

moment.locale('pt-br');

interface Props {
  documentId: string;
  /** Dentro de modal: sem borda superior, conteúdo sempre visível, sem acordeão. */
  variant?: 'default' | 'embedded';
}

function WorkflowTimelineIcon({ action }: { action: string }) {
  const tone = workflowActionTone(action);
  const cls =
    tone === 'green'
      ? 'text-green-600'
      : tone === 'red'
        ? 'text-red-600'
        : tone === 'blue'
          ? 'text-blue-600'
          : 'text-gray-500';
  switch (action) {
    case 'WorkflowStarted':
      return <PaperAirplaneIcon className={`h-5 w-5 ${cls}`} />;
    case 'WorkflowCancelled':
      return <XCircleIcon className={`h-5 w-5 ${cls}`} />;
    case 'ResponsibleChanged':
      return <PencilSquareIcon className={`h-5 w-5 ${cls}`} />;
    case 'Approve':
    case 'Publish':
    case 'Sign':
      return <CheckCircleIcon className={`h-5 w-5 ${cls}`} />;
    case 'Reject':
      return <XCircleIcon className={`h-5 w-5 ${cls}`} />;
    case 'SendForReview':
      return <PaperAirplaneIcon className={`h-5 w-5 ${cls}`} />;
    case 'SendForSignature':
    case 'SignatureInvitesSent':
      return <PaperAirplaneIcon className={`h-5 w-5 ${cls}`} />;
    case 'SignatureSigned':
    case 'SignaturesCompleted':
      return <CheckCircleIcon className={`h-5 w-5 ${cls}`} />;
    case 'SignatureRejected':
    case 'SignatureCancelled':
      return <XCircleIcon className={`h-5 w-5 ${cls}`} />;
    case 'CompleteEdit':
      return <PencilSquareIcon className={`h-5 w-5 ${cls}`} />;
    case 'Edit':
      return <PencilSquareIcon className={`h-5 w-5 ${cls}`} />;
    case 'Archive':
      return <ArchiveBoxIcon className={`h-5 w-5 ${cls}`} />;
    default:
      return <ArchiveBoxIcon className={`h-5 w-5 ${cls}`} />;
  }
}

export const WorkflowTimeline: React.FC<Props> = ({ documentId, variant = 'default' }) => {
  const embedded = variant === 'embedded';
  const storageKey = `workflow-timeline-${documentId}`;
  const [expanded, setExpanded] = useState(() => {
    if (embedded) return true;
    const v = localStorage.getItem(storageKey);
    if (v === null) return true;
    return v === 'true';
  });
  useEffect(() => {
    if (embedded) return;
    localStorage.setItem(storageKey, String(expanded));
  }, [expanded, storageKey, embedded]);

  const { addToast } = useToast();
  const [items, setItems] = useState<DocumentWorkflowAuditDto[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void documentWorkflowService
      .getAuditHistory(documentId)
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setItems([]);
          addToast(err instanceof Error ? err.message : 'Erro ao carregar histórico do workflow', 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [documentId, addToast]);

  return (
    <div className={embedded ? 'pt-0' : 'border-t pt-4'}>
      {!embedded && (
        <button
          type="button"
          className="mb-3 flex w-full items-center justify-between gap-2 text-left"
          onClick={() => setExpanded((e) => !e)}
        >
          <h3 className="text-sm font-medium text-gray-500">Histórico do Workflow</h3>
          <ChevronRightIcon
            className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </button>
      )}

      {(embedded || expanded) && (
        <div className="space-y-3">
          <div className="flex flex-wrap justify-end gap-2">
            <AuditCsvExportButton
              exportFn={() => documentWorkflowService.exportAuditCsv(documentId)}
              fileName={`auditoria-workflow-${documentId}.csv`}
            />
          </div>

          {loading && (
            <div className="space-y-4 py-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3 animate-pulse">
                  <div className="h-10 w-10 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-[75%] rounded bg-gray-200" />
                    <div className="h-3 w-[45%] rounded bg-gray-200" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && items && items.length === 0 && (
            <p className="text-sm text-gray-400 italic">Nenhuma ação registrada no workflow</p>
          )}

          {!loading && items && items.length > 0 && (
            <ul className="relative space-y-0 before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-px before:bg-gray-200">
              {items.map((row) => {
                const { trigger } = parseWorkflowAuditDetails(row.details);
                const scheduled = trigger === 'scheduled';
                const actor = row.userName?.trim() || 'Usuário';
                const actionText = workflowActionLabel(row.action);
                const isReject = row.action === 'Reject';
                const hasComment = Boolean(row.comment?.trim());
                const rel = moment(row.createdAt).fromNow();
                const full = moment(row.createdAt).format('LLL');

                return (
                  <li key={row.id} className="relative flex gap-3 pb-6 last:pb-0">
                    <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-gray-200">
                      <WorkflowTimelineIcon action={row.action} />
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{actor}</span>
                        {scheduled && (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                            Agendado
                          </span>
                        )}
                        <span
                          className="text-xs text-gray-500"
                          title={full}
                        >
                          {rel}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{actionText}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs">                        
                        {row.fromStatus && row.fromStatus != row.toStatus ? (
                          <>
                            <WorkflowStatusBadge status={row.fromStatus} />
                            <span className="text-gray-400">→</span>
                            <WorkflowStatusBadge status={row.toStatus} />
                          </>
                        )
                        : (
                          <WorkflowStatusBadge status={row.fromStatus} />
                        )
                      }
                      </div>
                      {(hasComment || isReject || row.action === 'SignatureRejected' || row.action === 'SignatureCancelled') && (
                        <div
                          className={`rounded-md border px-3 py-2 text-sm ${
                            (isReject || row.action === 'SignatureRejected' || row.action === 'SignatureCancelled') && !hasComment
                              ? 'border-amber-200 bg-amber-50 text-amber-900'
                              : 'border-blue-100 bg-blue-50/80 text-gray-800'
                          }`}
                        >
                          {hasComment
                            ? row.comment
                            : isReject
                              ? 'Rejeição sem comentário registrado.'
                              : row.action === 'SignatureRejected'
                                ? 'Recusa sem motivo registrado.'
                                : row.action === 'SignatureCancelled'
                                  ? 'Cancelamento sem motivo registrado.'
                                  : null}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
